import { useState } from 'react';

export interface PipelineStep {
  name: string;
  status: 'ok' | 'deny' | 'error' | 'allow' | 'approval' | 'pending' | 'transform' | 'skipped';
  durationMs?: number;
}

export interface PipelineStripProps {
  steps: PipelineStep[];
  className?: string;
  showLabels?: boolean;
  size?: 'sm' | 'md' | 'lg';
}

const STEP_LABELS: Record<string, string> = {
  auth: 'Auth',
  rate: 'Rate',
  anomaly: 'Anomaly',
  policy: 'Policy',
  dlp_args: 'DLP In',
  budget: 'Budget',
  execute: 'Exec',
  dlp_out: 'DLP Out',
  audit: 'Audit',
  cache: 'Cache',
};

const SIZE_MAP = {
  sm: 8,
  md: 12,
  lg: 16,
} as const;

function getStatusColor(status: PipelineStep['status']): string {
  switch (status) {
    case 'ok':
    case 'allow':
      return 'var(--accent-teal)';
    case 'deny':
    case 'error':
      return 'var(--color-error)';
    case 'approval':
    case 'pending':
      return 'var(--color-warning)';
    case 'transform':
      return 'var(--accent-violet)';
    case 'skipped':
      return 'var(--text-tertiary)';
  }
}

function getStatusLabel(status: PipelineStep['status']): string {
  switch (status) {
    case 'ok': return 'OK';
    case 'allow': return 'Allowed';
    case 'deny': return 'Denied';
    case 'error': return 'Error';
    case 'approval': return 'Approval Required';
    case 'pending': return 'Pending';
    case 'transform': return 'Transformed';
    case 'skipped': return 'Skipped';
  }
}

/** Returns true if the given status is a terminal one (processing stopped here). */
function isTerminal(status: PipelineStep['status']): boolean {
  return status === 'deny' || status === 'error' || status === 'approval' || status === 'pending';
}

/**
 * Find the index after which remaining steps were "not reached"
 * (i.e. the first terminal step). Returns -1 if all steps completed.
 */
function findTerminalIndex(steps: PipelineStep[]): number {
  for (let i = 0; i < steps.length; i++) {
    if (isTerminal(steps[i].status)) return i;
  }
  return -1;
}

export const DEFAULT_PIPELINE_STEPS: PipelineStep[] = [
  { name: 'auth', status: 'ok' },
  { name: 'rate', status: 'ok' },
  { name: 'anomaly', status: 'ok' },
  { name: 'policy', status: 'ok' },
  { name: 'dlp_args', status: 'ok' },
  { name: 'budget', status: 'ok' },
  { name: 'execute', status: 'ok' },
  { name: 'dlp_out', status: 'ok' },
  { name: 'audit', status: 'ok' },
  { name: 'cache', status: 'ok' },
];

export function PipelineStrip({
  steps,
  className = '',
  showLabels = false,
  size = 'md',
}: PipelineStripProps) {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const height = SIZE_MAP[size];
  const terminalIdx = findTerminalIndex(steps);

  return (
    <div className={`inline-flex flex-col ${className}`}>
      {/* Strip container */}
      <div
        className="relative flex items-center"
        style={{ gap: '2px' }}
      >
        {steps.map((step, i) => {
          const notReached = terminalIdx !== -1 && i > terminalIdx;
          const color = notReached
            ? 'var(--text-tertiary)'
            : getStatusColor(step.status);
          const opacity = notReached ? 0.35 : (step.status === 'skipped' ? 0.5 : 1);

          return (
            <div
              key={step.name}
              className="relative"
              onMouseEnter={() => setHoveredIndex(i)}
              onMouseLeave={() => setHoveredIndex(null)}
            >
              {/* Segment */}
              <div
                style={{
                  width: `${Math.max(200 / steps.length - 2, 8)}px`,
                  height: `${height}px`,
                  backgroundColor: color,
                  opacity,
                  borderRadius: '2px',
                  transition: 'opacity var(--transition-fast), background-color var(--transition-fast)',
                }}
              />

              {/* Tooltip */}
              {hoveredIndex === i && (
                <div
                  className="absolute z-50 whitespace-nowrap rounded-md px-2 py-1 text-xs font-medium animate-fade-in pointer-events-none"
                  style={{
                    bottom: `${height + 6}px`,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    backgroundColor: 'var(--surface-3)',
                    color: 'var(--text-primary)',
                    border: '1px solid var(--border)',
                    boxShadow: 'var(--shadow-md)',
                  }}
                >
                  <span style={{ color: 'var(--text-secondary)' }}>
                    {STEP_LABELS[step.name] || step.name}
                  </span>
                  {' '}
                  <span style={{ color }}>
                    {notReached ? 'Not reached' : getStatusLabel(step.status)}
                  </span>
                  {step.durationMs !== undefined && !notReached && (
                    <span style={{ color: 'var(--text-tertiary)', marginLeft: '4px' }}>
                      {step.durationMs}ms
                    </span>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Labels (optional) */}
      {showLabels && (
        <div
          className="flex items-start"
          style={{ gap: '2px', marginTop: '2px' }}
        >
          {steps.map((step, i) => {
            const notReached = terminalIdx !== -1 && i > terminalIdx;
            return (
              <div
                key={step.name}
                style={{
                  width: `${Math.max(200 / steps.length - 2, 8)}px`,
                  fontSize: '7px',
                  lineHeight: '10px',
                  textAlign: 'center',
                  color: notReached ? 'var(--border)' : 'var(--text-tertiary)',
                  fontFamily: 'var(--font-mono)',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {STEP_LABELS[step.name] || step.name}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
